import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import BottomSheet from "src/components/app/BottomSheet";
import Button from "src/components/app/Button";
import Input from "src/components/app/Input";
import LoaderFullScreen from "src/components/common/Loader/LoaderFullScreen";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as goalsActions from "src/redux/slices/goalsSlice";
import { IGoal } from "src/types/goals.types";

const goalTypes: Record<string, string> = {
  "water-logs": "water",
  "sleep-logs": "sleep",
  "steps-logs": "steps",
  "fatigue-logs": "fatigue",
  "diet-logs": "diet",
  "medication-logs": "medication",
  "exercise-logs": "exercise",
};

const validateGoals = {
  water: { min: 1, max: 5000, unit: "ml" },
  sleep: { min: 1, max: 24, unit: "hours" },
  steps: { min: 1, max: 25000, unit: "steps" },
  exercise: { min: 1, max: 10000, unit: "kcal" },
  fatigue: { min: 1, max: 10, unit: "" },
  diet: { min: 1, max: 10, unit: "meals" },
  medication: { min: 1, max: 20, unit: "times" },
} as const;

interface SetGoalBottomSheetProps {
  show: boolean;
  handleClose: () => void;
  /** Log key e.g. "water-logs", "sleep-logs" */
  type: string;
}

const SetGoalBottomSheet: React.FC<SetGoalBottomSheetProps> = ({
  show,
  handleClose,
  type,
}) => {
  const goalType = goalTypes[type] ?? type;
  const [goal, setGoal] = useState<IGoal | null>(null);
  const [goalValue, setGoalValue] = useState(0);
  const [goalError, setGoalError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const { patient } = useAppSelector((state) => state.auth);
  const { goalsData } = useAppSelector((state) => state.goals);

  const validation = validateGoals[goalType as keyof typeof validateGoals];

  useEffect(() => {
    if (patient?.data?.patient_id) {
      dispatch(
        goalsActions.getGoalsRequest({
          payload: { user_id: patient?.data?.patient_id },
          resolve: () => {},
          reject: () => {},
        })
      );
    }
  }, [patient?.data?.patient_id, dispatch]);

  useEffect(() => {
    if (goalsData?.data?.goals && goalType) {
      const goalData = goalsData.data.goals.find(
        (g) => g.goal_type === `${goalType}_goal`
      );
      setGoal(goalData || null);
      setGoalValue(Math.abs(goalData?.goal_value || 0));
    }
  }, [goalsData, goalType]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const invalidKeys = ["-", "+", "e", "E", ".", ",", " "];
    if (
      invalidKeys.includes(e.key) ||
      (!/[0-9]/.test(e.key) && e.key.length === 1)
    ) {
      e.preventDefault();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value === "") {
      setGoalValue(0);
      setGoalError(null);
      return;
    }
    if (!/^\d+$/.test(value)) {
      setGoalError("Only positive numbers are allowed");
      return;
    }
    const numericValue = Number(value);
    if (!validation) {
      setGoalValue(numericValue);
      setGoalError(null);
      return;
    }
    const { min, max, unit } = validation;
    const errorMessage =
      numericValue < min || numericValue > max
        ? `Please enter the value between ${min} to ${max} ${unit}`
        : null;
    setGoalError(errorMessage);
    setGoalValue(numericValue);
  };

  const handleSave = () => {
    if (goalError || !patient?.data?.patient_id) return;
    const payload = {
      user_id: patient.data.patient_id,
      [`${goalType}_goal`]: goalValue,
    };
    new Promise((resolve, reject) => {
      dispatch(
        goalsActions.setGoalsRequest({
          payload: JSON.stringify(payload),
          resolve,
          reject,
        })
      );
    })
      .then(() => {
        handleClose();
        toast.success("Goals set successfully");
      })
      .catch(() => {
        toast.error("Failed to set goals!");
      });
  };

  const title = validation
    ? `Set ${goalType.charAt(0).toUpperCase() + goalType.slice(1)} Goal`
    : "Set Goal";

  if (goalsData?.isLoading) {
    return <LoaderFullScreen show={true} loaderSize="lg" />;
  }

  return (
    <BottomSheet
      show={show}
      handleClose={handleClose}
      title={title}
      showCloseButton
    >
      <div className="p-4 space-y-4">
        {validation ? (
          <>
            <div className="relative w-full">
              <Input
                label={`Daily goal for ${
                  goalType.charAt(0).toUpperCase() + goalType.slice(1)
                }`}
                name="goal"
                type="number"
                placeholder="Enter value"
                fullWidth
                value={String(goalValue || "")}
                onKeyDown={handleKeyDown}
                onChange={handleChange}
                pattern="[0-9]*"
                endAdornment={
                  goal?.goal_unit ? (
                    <span className="text-sm text-gray-500 px-2">
                      {goal.goal_unit}
                    </span>
                  ) : validation.unit ? (
                    <span className="text-sm text-gray-500 px-2">
                      {validation.unit}
                    </span>
                  ) : undefined
                }
                error={!!goalError}
                helperText={goalError || ""}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outlined"
                rounded="full"
                fullWidth
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                rounded="full"
                fullWidth
                onClick={handleSave}
                disabled={goalValue === 0 || !!goalError}
              >
                Save
              </Button>
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-500">
            Goal setting is not available for this type.
          </p>
        )}
      </div>
    </BottomSheet>
  );
};

export default SetGoalBottomSheet;
